// The Workspaces page as an inventory of the team's finding investigations:
// what each is, whose it is, when it was opened and when it was last worked on. Nothing
// here calls the network; components/WorkspaceInventory.jsx reads and draws.
import { INBOX_SCOPE } from "./caseInbox.js";
import { formatAgo } from "./escalationLabels.js";
import { caseState } from "./findingsConsole.js";
import { caseCanvasId } from "./traceCanvasStore.js";

const DAY = 86_400_000;
const ON_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const ON_DATE_YEAR = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });
const lower = (v) => String(v ?? "").trim().toLowerCase();

// What each column sorts on. Dates sort newest first; the unassigned sort
// after every owner.
const SORT_KEY = {
  title: () => (inv) => lower(inv.title),
  owner: (nameOf) => (inv) => (inv.ownerUserId ? lower(nameOf(inv.ownerUserId)) : "￿"),
  opened: () => (inv) => -(inv.createdAtMs || 0),
  activity: () => (inv) => -(inv.updatedAtMs || 0),
};
export const SORTS = Object.keys(SORT_KEY);

/**
 * The investigations listed: the open ones, and the closed ones too with
 * `closed`, sorted by a column (`by`, one of SORTS; `reverse` flips it).
 * `nameOf(userId)` names an owner for sorting by owner.
 */
export function listed(byId, { closed = false, by = "activity", reverse = false, nameOf = (id) => id } = {}) {
  const key = (SORT_KEY[by] || SORT_KEY.activity)(nameOf);
  const rows = [...(byId?.values() || [])].filter((inv) => closed || inv.status !== "closed");
  rows.sort((a, b) => {
    const x = key(a);
    const y = key(b);
    return (x < y ? -1 : x > y ? 1 : 0) || String(a.incidentId).localeCompare(String(b.incidentId));
  });
  return reverse ? rows.reverse() : rows;
}

/** Where a closed investigation ended, in words; empty while it is open. */
export function statusWords(inv) {
  if (inv?.status !== "closed") return "";
  const s = caseState(inv);
  if (s.state === "tp") return "Closed as a true positive";
  if (s.state === "joined") return `Joined to ${s.joined.title}`;
  if (s.reason === "mixed") return "Closed with a mixed outcome";
  if (s.reason === "inconclusive") return "Closed as inconclusive";
  if (s.reason === "other") return "Rejected";
  return "Closed as a false positive";
}

/** A day, with its year when it is not this one. */
export function onDate(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  return (new Date(ms).getFullYear() === new Date(now).getFullYear() ? ON_DATE : ON_DATE_YEAR).format(ms);
}

/** When, in words: how long ago within two weeks, the date before. */
export function when(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  return now - ms < 14 * DAY ? formatAgo(new Date(ms), now) : onDate(ms, now);
}

/**
 * The board this browser keeps for an investigation, from its members
 * (`findings` of a Get): the board of the case a member was saved from, else
 * a case board holding one of its findings. `boards`: the boards kept here,
 * each { id, findings: [finding id] }. Null when there is none.
 */
export function boardOf(members, boards, investigationId = null) {
  if (investigationId) {
    const bound = boards.find((b) => b.investigationId === investigationId);
    if (bound) return bound.id;
    boards = boards.filter((b) => !b.investigationId);
  }
  const kept = new Set(boards.map((b) => b.id));
  for (const m of members || []) {
    const id = caseCanvasId(INBOX_SCOPE, m.source?.case);
    if (id && kept.has(id)) return id;
  }
  const ids = new Set((members || []).map((m) => lower(m.finding_id)).filter(Boolean));
  return boards.find((b) => b.id.startsWith("case") && b.findings.some((f) => ids.has(lower(f))))?.id || null;
}
